import { Image, CalendarDays, ShoppingBag, Phone } from 'lucide-react';
import { getProducerNavVisibility } from '@/config/nav.producer';

export interface NextStepInput {
  hasCover: boolean;
  upcomingCount: number;
  shopEnabled: boolean;
  productCount: number;
  hasContact: boolean;
  /** Active offering keys — used for soft gating activity step */
  offeringKeys?: string[];
}

export interface NextStepResult {
  key: 'cover' | 'events' | 'products' | 'contact';
  titleSv: string;
  bodySv: string;
  ctaLabelSv: string;
  hrefSuffix: string;
  icon: React.ElementType;
}

/**
 * Returns exactly one recommended next step for the producer,
 * or null if everything looks good.
 * Priority order: cover → events (offering-aware) → products (if shop) → contact.
 */
export function getNextStep(input: NextStepInput): NextStepResult | null {
  if (!input.hasCover) {
    return {
      key: 'cover',
      titleSv: 'Lägg till en omslagsbild',
      bodySv: 'En bra bild gör att fler stannar och utforskar er sida.',
      ctaLabelSv: 'Lägg till bild',
      hrefSuffix: '/plats',
      icon: Image,
    };
  }

  if (input.upcomingCount === 0) {
    const vis = getProducerNavVisibility(input.offeringKeys ?? []);
    // Only suggest activity creation prominently if offerings signal it
    // or if no offerings are set yet (new producer)
    if (vis.showActivities || (input.offeringKeys ?? []).length === 0) {
      return {
        key: 'events',
        titleSv: 'Skapa en aktivitet',
        bodySv: 'Aktiviteter gör det enkelt för besökare att hitta er.',
        ctaLabelSv: 'Skapa aktivitet',
        hrefSuffix: '/events/new',
        icon: CalendarDays,
      };
    }
    // Non-activity offerings → skip this step entirely, don't nag
  }

  if (input.shopEnabled && input.productCount === 0) {
    return {
      key: 'products',
      titleSv: 'Lägg till er första produkt',
      bodySv: 'Er gårdsbutik är aktiv men saknar produkter.',
      ctaLabelSv: 'Lägg till produkt',
      hrefSuffix: '/shop/products',
      icon: ShoppingBag,
    };
  }

  if (!input.hasContact) {
    return {
      key: 'contact',
      titleSv: 'Lägg till kontaktuppgifter',
      bodySv: 'Gör det lätt för besökare att nå er.',
      ctaLabelSv: 'Redigera kontakt',
      hrefSuffix: '/plats',
      icon: Phone,
    };
  }

  return null;
}
